import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Upload, Image as ImageIcon, X, Loader2, Trash2, Grid, List, Tag, ArrowLeft, Download, Archive, Eye, EyeOff } from 'lucide-react';
import api from '../../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageLibraryPanel = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [dragActive, setDragActive] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [disclaimerEnabled, setDisclaimerEnabled] = useState(true);
  const [savingDisclaimer, setSavingDisclaimer] = useState(false);
  const fileInputRef = useRef(null);

  // Load disclaimer banner state on mount
  useEffect(() => {
    fetch(`${API_URL}/api/admin/image-disclaimer`)
      .then(r => r.ok ? r.json() : { enabled: true })
      .then(({ enabled }) => setDisclaimerEnabled(enabled !== false))
      .catch(() => {});
  }, []);

  const toggleDisclaimer = async () => {
    setSavingDisclaimer(true);
    const next = !disclaimerEnabled;
    try {
      await api.put('/admin/image-disclaimer', { enabled: next });
      setDisclaimerEnabled(next);
      toast.success(next ? 'Banner "imágenes orientativas" activado' : 'Banner desactivado');
    } catch (e) {
      toast.error('No se pudo actualizar el banner');
    } finally {
      setSavingDisclaimer(false);
    }
  };

  const fetchImages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/images?limit=200`);
      const data = await res.json();
      setImages(data.images || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Error al cargar imágenes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    let uploaded = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/images/upload`, { method: 'POST', body: formData });
        if (res.ok) {
          uploaded++;
          setUploadProgress(Math.round((uploaded / files.length) * 100));
        } else {
          const err = await res.json();
          toast.error(`Error: ${err.detail || file.name}`);
        }
      } catch (error) {
        toast.error(`Error subiendo ${file.name}`);
      }
    }

    if (uploaded > 0) {
      toast.success(`${uploaded} imagen(es) subida(s)`);
      fetchImages();
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) handleUpload(Array.from(e.dataTransfer.files));
  };

  const handleFileChange = (e) => {
    if (e.target.files?.length > 0) handleUpload(Array.from(e.target.files));
    e.target.value = '';
  };

  const handleDelete = async (imageId, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar esta imagen?')) return;
    try {
      const res = await fetch(`${API_URL}/api/images/${imageId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Imagen eliminada');
        setImages(prev => prev.filter(i => i.id !== imageId));
        if (lightboxImage?.id === imageId) setLightboxImage(null);
      } else {
        toast.error('Error al eliminar');
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const copyUrl = (img) => {
    const url = `${API_URL}${img.url}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copiada');
  };

  const downloadImage = async (img, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await fetch(getFullUrl(img.url));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = img.original_name || `imagen_${img.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Error al descargar');
    }
  };

  const [downloadingZip, setDownloadingZip] = useState(false);
  const [downloadingProducts, setDownloadingProducts] = useState(false);
  const downloadAllZip = async () => {
    setDownloadingZip(true);
    try {
      const res = await fetch(`${API_URL}/api/images/download-all`);
      if (!res.ok) throw new Error('zip failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '');
      a.download = `pont10_imagenes_${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('ZIP descargado');
    } catch (err) {
      toast.error('Error al descargar ZIP');
    } finally {
      setDownloadingZip(false);
    }
  };

  const downloadProductsImages = async () => {
    setDownloadingProducts(true);
    toast.info('Preparando ZIP de imágenes de productos… puede tardar 1-2 min', { duration: 4000 });
    try {
      const res = await fetch(`${API_URL}/api/images/download-products`);
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '');
      a.download = `pont10_productos_imagenes_${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('ZIP de productos descargado');
    } catch (err) {
      toast.error('Error al descargar ZIP de productos');
    } finally {
      setDownloadingProducts(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFullUrl = (url) => url?.startsWith('http') ? url : `${API_URL}${url}`;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6" data-testid="image-library-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="image-library-title">
            <ImageIcon size={24} />
            Biblioteca de Imágenes
          </h1>
          <p className="text-sm text-gray-500 mt-1">{images.length} imágenes</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline" size="sm"
            onClick={toggleDisclaimer}
            disabled={savingDisclaimer}
            className={`h-9 gap-2 ${disclaimerEnabled ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}
            data-testid="disclaimer-toggle-btn"
            title='Activa o desactiva el banner "Las imágenes son orientativas" que ven los clientes'
          >
            {savingDisclaimer ? <Loader2 size={14} className="animate-spin" /> : disclaimerEnabled ? <Eye size={14} /> : <EyeOff size={14} />}
            <span className="hidden sm:inline">Banner orientativo: {disclaimerEnabled ? 'ON' : 'OFF'}</span>
            <span className="sm:hidden">{disclaimerEnabled ? 'ON' : 'OFF'}</span>
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={downloadProductsImages}
            disabled={downloadingProducts}
            className="h-9 gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="download-products-zip-btn"
            title="Descarga todas las imágenes que están asignadas a productos (incluidas las externas)"
          >
            {downloadingProducts ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            <span className="hidden sm:inline">Imágenes de productos (ZIP)</span>
            <span className="sm:hidden">Productos</span>
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={downloadAllZip}
            disabled={downloadingZip || images.length === 0}
            className="h-9 gap-2"
            data-testid="download-all-zip-btn"
            title="Descarga sólo las imágenes que están en esta biblioteca"
          >
            {downloadingZip ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />}
            <span className="hidden sm:inline">Biblioteca (ZIP)</span>
            <span className="sm:hidden">ZIP</span>
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'} size="sm"
            onClick={() => setViewMode('grid')} className="h-9 w-9 p-0"
            data-testid="view-grid-btn"
          >
            <Grid size={16} />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'} size="sm"
            onClick={() => setViewMode('list')} className="h-9 w-9 p-0"
            data-testid="view-list-btn"
          >
            <List size={16} />
          </Button>
        </div>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all mb-6 ${
          dragActive ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
        }`}
        onDragEnter={handleDrag} onDragLeave={handleDrag}
        onDragOver={handleDrag} onDrop={handleDrop}
        data-testid="upload-area"
      >
        <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />

        {uploading ? (
          <div className="space-y-3">
            <Loader2 size={32} className="mx-auto animate-spin text-amber-600" />
            <p className="text-sm text-gray-600">Subiendo imágenes... {uploadProgress}%</p>
            <div className="w-48 mx-auto h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-50 flex items-center justify-center">
              <Upload size={28} className="text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-700 font-medium">
                Arrastra imágenes aquí o{' '}
                <button onClick={() => fileInputRef.current?.click()}
                  className="text-amber-600 hover:text-amber-700 font-semibold underline"
                  data-testid="select-files-btn"
                >
                  selecciona archivos
                </button>
              </p>
              <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WebP — máx. 10MB por imagen</p>
            </div>
          </div>
        )}
      </div>

      {/* Image Grid/List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-gray-400" />
        </div>
      ) : images.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <ImageIcon size={56} className="mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">No hay imágenes</p>
          <p className="text-sm">Sube tu primera imagen para empezar</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="images-grid">
          {images.map((img) => (
            <div
              key={img.id}
              onClick={() => setLightboxImage(img)}
              className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group border border-gray-100 hover:border-amber-300 transition-all hover:shadow-md"
              data-testid={`image-card-${img.id}`}
            >
              <img src={getFullUrl(img.url)} alt={img.original_name}
                className="w-full h-full object-cover" loading="lazy" decoding="async" />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-end">
                <div className="w-full p-2 translate-y-full group-hover:translate-y-0 transition-transform">
                  <p className="text-white text-xs truncate font-medium">{img.original_name}</p>
                  <p className="text-white/70 text-xs">{formatSize(img.size)}</p>
                </div>
              </div>
              {/* Action buttons */}
              <button onClick={(e) => downloadImage(img, e)}
                className="absolute top-2 left-2 w-7 h-7 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`download-image-${img.id}`}
                title="Descargar"
              >
                <Download size={13} className="text-white" />
              </button>
              <button onClick={(e) => handleDelete(img.id, e)}
                className="absolute top-2 right-2 w-7 h-7 bg-red-500/80 hover:bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`delete-image-${img.id}`}
              >
                <Trash2 size={13} className="text-white" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2" data-testid="images-list">
          {images.map((img) => (
            <div key={img.id}
              onClick={() => setLightboxImage(img)}
              className="flex items-center gap-4 p-3 rounded-xl border border-gray-100 hover:border-amber-200 hover:bg-amber-50/30 cursor-pointer group transition-all"
              data-testid={`image-row-${img.id}`}
            >
              <img src={getFullUrl(img.url)} alt={img.original_name}
                className="w-14 h-14 object-cover rounded-lg" loading="lazy" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-gray-800">{img.original_name}</p>
                <p className="text-xs text-gray-400">{formatSize(img.size)} — {img.created_at?.split('T')[0]}</p>
              </div>
              <button onClick={(e) => { e.stopPropagation(); copyUrl(img); }}
                className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
              >
                Copiar URL
              </button>
              <button onClick={(e) => downloadImage(img, e)}
                data-testid={`download-image-list-${img.id}`}
                className="p-1.5 text-gray-400 hover:text-amber-600 transition-colors"
                title="Descargar"
              >
                <Download size={16} />
              </button>
              <button onClick={(e) => handleDelete(img.id, e)}
                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setLightboxImage(null)} data-testid="image-lightbox"
        >
          <div className="relative max-w-3xl w-full max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <img src={getFullUrl(lightboxImage.url)} alt={lightboxImage.original_name}
              className="w-full h-auto max-h-[70vh] object-contain rounded-xl" />
            <div className="mt-3 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">{lightboxImage.original_name}</p>
                <p className="text-white/60 text-sm">{formatSize(lightboxImage.size)}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-white border-white/30 hover:bg-white/10"
                  onClick={() => copyUrl(lightboxImage)}>
                  Copiar URL
                </Button>
                <Button size="sm" variant="outline" className="text-white border-white/30 hover:bg-white/10"
                  onClick={(e) => downloadImage(lightboxImage, e)}>
                  <Download size={14} className="mr-1" /> Descargar
                </Button>
                <Button size="sm" variant="outline" className="text-red-400 border-red-400/30 hover:bg-red-500/10"
                  onClick={(e) => handleDelete(lightboxImage.id, e)}>
                  <Trash2 size={14} className="mr-1" /> Eliminar
                </Button>
              </div>
            </div>
            <button onClick={() => setLightboxImage(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg"
            >
              <X size={16} className="text-gray-700" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageLibraryPanel;
